import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { setGA4Event } from "@tggstore/Handlers/Utilities/CustomTracking";
import clearProductCategories from "@tggstore/Store/Handlers/Custom/ClearProductCategoires";
import loadProductCategories from "@tggstore/Store/Handlers/Custom/LoadProductCategories";
import { CustomState } from "@tggstore/Store/Reducers";
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

export const useOrderConfirmationTracking = (cartId: string) => {
    const dispatch = useDispatch();
    const hasTrackedGA4 = useRef(false);
    const hasLoadedProductCategories = useRef(false);
    const isProcessing = useRef(false);

    const trackingData = useSelector((state: CustomState) => {
        const session = state.context.session;
        const cartState = getCartState(state, cartId);
        const cartLines = cartState.value?.cartLines;
        const currency = session?.currency?.currencyCode || "";
        const billToCustomerName = getBillToState(state, cartState.value?.billToId)?.value?.customerNumber ?? "";
        const transactionType = state.customTransactionType.transactionType || "";

        // TODO: Add promotions tracking
        // const promotionsDataView = cartState.value
        //     ? getPromotionsDataView(state, cartState.value.id)
        //     : getCurrentPromotionsDataView(state);
        // const promotions = promotionsDataView.value;

        // const promotionApplied = promotions?.some(promotion => promotion.promotionApplied === true);
        // const promotionCode = promotions?.find(promotion => promotion.promotionApplied === true)?.promotionCode;

        return {
            cartState,
            cartLines,
            productCategories: state.customProductCategories.productProperties,
            currency,
            billToCustomerName,
            affiliation: state.customDefaultWarehouse.warehouse?.description || "",
            orderSubTotal: cartState.value?.orderSubTotal || 0,
            shipping: cartState.value?.shippingAndHandling || 0,
            tax: cartState.value?.totalTax || 0,
            transactionType,
            shouldLoadProductCategories:
                state.customProductCategories.productProperties.length === 0 ||
                state.customProductCategories.productProperties.length === undefined,

            orderNumber: cartState.value?.orderNumber,
        };
    });

    // Effect for loading product categories
    useEffect(() => {
        const { cartLines, shouldLoadProductCategories } = trackingData;

        if (
            !cartLines ||
            cartLines.length === 0 ||
            !shouldLoadProductCategories ||
            hasLoadedProductCategories.current
        ) {
            return;
        }

        hasLoadedProductCategories.current = true;
        dispatch(
            loadProductCategories({
                productIds: cartLines.map(o => o.productId).filter((id): id is string => id !== null),
                type: "cart",
            }) as any,
        );
    }, [dispatch, trackingData.cartLines, trackingData.shouldLoadProductCategories]);

    // Effect for GA4 tracking
    useEffect(() => {
        const {
            cartLines,
            productCategories,
            cartState,
            orderNumber,
            transactionType,
            currency,
            orderSubTotal,
            affiliation,
            billToCustomerName,
            tax,
            shipping,
        } = trackingData;

        // Prevent multiple triggers with these checks
        if (
            hasTrackedGA4.current ||
            isProcessing.current ||
            !cartLines ||
            cartLines.length === 0 ||
            !orderNumber ||
            !transactionType ||
            productCategories.length === 0
        ) {
            return;
        }

        isProcessing.current = true;

        try {
            setGA4Event(cartLines, "purchase", {
                currency,
                value: orderSubTotal,
                affiliation,
                account_number: billToCustomerName,
                productCategories,
                tax,
                shipping,
                transaction_id: orderNumber,
                transaction_type: transactionType.name,
            });

            hasTrackedGA4.current = true;
            dispatch(clearProductCategories({}) as any);
        } finally {
            isProcessing.current = false;
        }
    }, [dispatch, trackingData.productCategories.length, trackingData.orderNumber, trackingData.transactionType]);
};
